import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Shield, Mail, Lock, ArrowLeft } from 'lucide-react';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (!user.is_admin) {
        toast.error('Admin-Zugriff erforderlich');
        return;
      }
      toast.success('Admin-Anmeldung erfolgreich');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.status === 401 ? 'E-Mail oder Passwort ist falsch' : 'Anmeldung ist derzeit nicht möglich');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-slate-300 hover:text-white"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Startseite
        </Button>

        <Card className="p-8 shadow-2xl border-slate-700 bg-slate-800">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_friseur-booking/artifacts/mtmj2lif_IMG_5702.PNG" 
                alt="Salon Galo Logo" 
                className="h-16 w-auto bg-white p-2 rounded-lg"
              />
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-white">Admin Login</h1>
            <p className="text-slate-400 mt-2">Nur für autorisierte Administratoren</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-300">
                E-Mail
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 rounded-lg bg-slate-700 border-slate-600 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="name@salon.de"
                  required
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-300">
                Passwort
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 rounded-lg bg-slate-700 border-slate-600 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-full text-base font-medium"
              data-testid="admin-login-submit"
            >
              {loading ? 'Anmelden...' : 'Als Admin anmelden'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-400">
              Kein Admin?{' '}
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
                Normaler Login
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminLoginPage;
